import axios from "axios";
import { api, adminApi, brandApi } from "../lib/axios";
import type {
  AdminCustomJoinPage,
  CustomJoinPageStatus,
  OAuthConnection,
  OAuthConsentRequest,
  CustomJoinPageState,
  JoinPageReportReason,
  JoinPageSpec,
  JoinPageVersion,
  JoinPageVersionSummary,
  LintResponse,
  PublishBlockedBody,
  PublishResponse,
} from "../types/customJoinPage";

/** Thrown by publish when the server refused because of lint/content issues. */
export class PublishBlockedError extends Error {
  constructor(public readonly body: PublishBlockedBody) {
    super(body.message);
    this.name = "PublishBlockedError";
  }
}

function messageOf(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === "string") return data.message;
  }
  return error instanceof Error ? error.message : fallback;
}

export const customJoinPageService = {
  // ---- public ----------------------------------------------------------

  async getSpec(): Promise<JoinPageSpec> {
    const { data } = await api.get<JoinPageSpec>("/custom-join-page/spec");
    return data;
  },

  /** The spec with this brand's palette and token values folded in. */
  async getPrompt(): Promise<string> {
    const { data } = await brandApi.get<{ version: number; markdown: string }>(
      "/custom-join-page/me/prompt"
    );
    return data.markdown;
  },

  async report(
    username: string,
    body: { reason: JoinPageReportReason; details?: string; email?: string }
  ): Promise<{ received: boolean }> {
    const { data } = await api.post(`/custom-join-page/report/${username}`, body);
    return data;
  },

  // ---- brand -----------------------------------------------------------

  async getMine(): Promise<CustomJoinPageState> {
    const { data } = await brandApi.get<CustomJoinPageState>("/custom-join-page/me");
    return data;
  },

  async lint(html: string, css: string): Promise<LintResponse> {
    const { data } = await brandApi.post<LintResponse>("/custom-join-page/me/lint", { html, css });
    return data;
  },

  async saveDraft(html: string, css: string): Promise<{ savedAt: string }> {
    try {
      const { data } = await brandApi.put("/custom-join-page/me/draft", { html, css });
      return data;
    } catch (error) {
      throw new Error(messageOf(error, "Failed to save draft"));
    }
  },

  async publish(source?: { html: string; css: string }): Promise<PublishResponse> {
    try {
      const { data } = await brandApi.post<PublishResponse>(
        "/custom-join-page/me/publish",
        source ?? {}
      );
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const body = error.response.data as PublishBlockedBody;
        if (Array.isArray(body?.errors)) throw new PublishBlockedError(body);
      }
      throw new Error(messageOf(error, "Failed to publish"));
    }
  },

  async unpublish(): Promise<{ status: "DRAFT" }> {
    try {
      const { data } = await brandApi.post("/custom-join-page/me/unpublish");
      return data;
    } catch (error) {
      throw new Error(messageOf(error, "Failed to unpublish"));
    }
  },

  /** Standard ⇄ Custom switch. `true` re-activates the last published version. */
  async setLive(live: boolean): Promise<{ status: CustomJoinPageStatus }> {
    try {
      const { data } = await brandApi.put("/custom-join-page/me/live", { live });
      return data;
    } catch (error) {
      throw new Error(messageOf(error, "Couldn't switch the live join page"));
    }
  },

  async versions(): Promise<JoinPageVersionSummary[]> {
    const { data } = await brandApi.get("/custom-join-page/me/versions");
    return data;
  },

  async getVersion(version: number): Promise<JoinPageVersion> {
    const { data } = await brandApi.get(`/custom-join-page/me/versions/${version}`);
    return data;
  },

  async rollback(version: number): Promise<PublishResponse> {
    try {
      const { data } = await brandApi.post(`/custom-join-page/me/rollback/${version}`);
      return data;
    } catch (error) {
      throw new Error(messageOf(error, "Failed to restore version"));
    }
  },

  // ---- OAuth (MCP connector) --------------------------------------------

  async describeOAuthRequest(id: string): Promise<OAuthConsentRequest> {
    try {
      const { data } = await brandApi.get(`/oauth/requests/${id}`);
      return data;
    } catch (error) {
      throw new Error(messageOf(error, "This authorization request has expired."));
    }
  },

  async approveOAuthRequest(id: string): Promise<{ redirectTo: string }> {
    try {
      const { data } = await brandApi.post(`/oauth/requests/${id}/approve`);
      return data;
    } catch (error) {
      throw new Error(messageOf(error, "Could not approve the request."));
    }
  },

  async denyOAuthRequest(id: string): Promise<{ redirectTo: string }> {
    try {
      const { data } = await brandApi.post(`/oauth/requests/${id}/deny`);
      return data;
    } catch (error) {
      throw new Error(messageOf(error, "Could not deny the request."));
    }
  },

  async listConnections(): Promise<OAuthConnection[]> {
    const { data } = await brandApi.get("/oauth/me/connections");
    return data;
  },

  async revokeConnection(clientId: string): Promise<void> {
    try {
      await brandApi.delete(`/oauth/me/connections/${clientId}`);
    } catch (error) {
      throw new Error(messageOf(error, "Could not disconnect."));
    }
  },

  // ---- admin -----------------------------------------------------------

  async adminSetEnabled(
    brandId: string,
    enabled: boolean,
    note?: string | null
  ): Promise<AdminCustomJoinPage & { brandDetailId: string; isLive: boolean }> {
    try {
      const { data } = await adminApi.patch(`/admin/brands/${brandId}/custom-join-page`, {
        enabled,
        ...(note !== undefined ? { note } : {}),
      });
      return {
        brandDetailId: data.brandDetailId,
        enabled: data.enabledByAdmin,
        status: data.status,
        publishedVersion: data.publishedVersion,
        note: data.adminNote ?? null,
        openReports: 0,
        isLive: data.isLive,
      };
    } catch (error) {
      throw new Error(messageOf(error, "Failed to update custom join page access"));
    }
  },
};
